import React, { useEffect, useState, Fragment } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { getAsset, updateAsset } from 'state/assets/AssetActions';
import Input from 'components/Form/Input';
import * as assetTypes from 'state/assets/AssetTypes';
import isAuthenticated from 'hooks/isAuthenticated';
import {
    InputChange,
    SelectChange,
    TextAreaChange,
    FormSubmission,
    Error,
    ErrorCodes,
} from '../../types';
import Form from 'components/Form';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import ErrorMessage from 'components/ErrorMessage';
import FormHeader from 'components/FormHeader';
import Legend from 'components/Legend';
import styled from 'styled-components';
import TierSelect from 'components/TierSelect';
import TextArea from 'components/TextArea';
import { SubscriptionTiers } from 'state/subscription/SubscriptionTypes';
import Button from 'components/Button';
import Loading from 'components/Loading';
import BackButton from 'components/BackButton';
import { validateBytes, presentErrorBasedOnCode } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'state/store';
import { selectSelectedAsset } from 'state/assets/AssetSelectors';
import { CLEAR_SELECTED_ASSET } from 'state/assets/AssetActionTypes';

interface Props extends RouteComponentProps {
    id?: string;
}

const WaveFormEdit: React.FC<Props> = ({ id }) => {
    const [form, setForm] = useState<assetTypes.UpdateAssetRequest>({
        name: '',
        tier: undefined,
        bytes: [],
    });
    const waveFormToEdit: assetTypes.Asset | null = useSelector((state: AppState) =>
        selectSelectedAsset(state)
    );
    const [loading, setLoading] = useState<boolean>(true);
    const adminRoleRequired = true;
    const authenticated: boolean = isAuthenticated(adminRoleRequired);
    const [error, setError] = useState<Error | null>(null);
    const [formComplete, setFormComplete] = useState<boolean>(true);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [byteStrings, setByteStrings] = useState<string>('');
    const [byteError, setByteError] = useState<boolean>(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!!form.name && !!form.tier && !!byteStrings) {
            setFormComplete(true);
        } else {
            setFormComplete(false);
        }
    }, [form]);

    useEffect(() => {
        (async () => {
            if (waveFormToEdit) {
                await setForm({ ...waveFormToEdit });
                await setByteStrings(waveFormToEdit.bytes.join(', '));
                setLoading(false);
            }
        })();
    }, [waveFormToEdit]);

    useEffect(() => {
        (async () => {
            if (authenticated && id) {
                try {
                    await dispatch(getAsset(id));
                } catch (error) {
                    //if error results from missing token it will not have a json method
                    error = error.json ? await error.json() : error;
                    setError(error);
                }
            }
        })();

        return () => {
            dispatch({ type: CLEAR_SELECTED_ASSET });
        };
    }, [id]);
    const handleInput = (e: InputChange | SelectChange | TextAreaChange): void => {
        e.persist();
        setForm(form => ({ ...form, [e.target.name]: e.target.value }));
    };

    const handleBytesInput = (e: TextAreaChange): void => {
        e.persist();
        setByteStrings(e.target.value);
        setForm(form => ({ ...form, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: FormSubmission): Promise<void> => {
        e.preventDefault();
        setIsUpdating(true);
        const bytes: number[] | null = validateBytes(byteStrings, 12);
        if (authenticated && bytes) {
            try {
                await dispatch(updateAsset({ ...form, bytes }));
                byteError && setByteError(false);
            } catch (error) {
                //if error results from missing token it will not have a json method
                error = error.json ? await error.json() : error;
                setError(error);
            }
        } else {
            setByteError(true);
        }
        setIsUpdating(false);
        waveFormToEdit && navigate(`/waveforms/${waveFormToEdit._id}`);
    };
    return (
        <Container margin="0 auto">
            <BackButton to="/waveforms" />
            {loading ? (
                <Loading />
            ) : (
                <Fragment>
                    <FormContainer>
                        <Header>EDIT WAVEFORM</Header>
                        <Legend>
                            Fields with <span>*</span> are required
                        </Legend>
                        <Form onSubmit={handleSubmit}>
                            <Input
                                label="Name"
                                value={form.name}
                                name="name"
                                onChange={handleInput}
                                margin="1em auto"
                                size="full"
                                errorMessage="Waveform name is required"
                                type="text"
                                required={true}
                            />
                            <TierSelect
                                label="Tier"
                                name="tier"
                                value={form.tier}
                                onChange={handleInput}
                                required={true}>
                                <option value={SubscriptionTiers.Free}>
                                    {SubscriptionTiers.Free}
                                </option>
                                <option value={SubscriptionTiers.Paid}>
                                    {SubscriptionTiers.Paid}
                                </option>
                            </TierSelect>
                            <ToolTip>
                                Please enter bytes as comma separated numbers (128, 256, etc)
                            </ToolTip>
                            <TextArea
                                placeholder="Waveform bytes"
                                label="Bytes"
                                value={byteStrings}
                                name="bytes"
                                onChange={e => handleBytesInput(e)}
                                required={true}
                                errorMessage={
                                    byteError &&
                                    'Please ensure there are 12 bytes between 0-255, whole numbers or valid hexadecimals'
                                }
                            />
                            <Button isLoading={isUpdating} disabled={!formComplete || loading}>
                                UPDATE
                            </Button>
                        </Form>
                        {error && (
                            <ErrorMessage>
                                {presentErrorBasedOnCode(error.code as ErrorCodes)}
                            </ErrorMessage>
                        )}
                    </FormContainer>
                </Fragment>
            )}
        </Container>
    );
};

export default WaveFormEdit;

const Header = styled(FormHeader)`
    margin: 0 0 1em 0;
`;

const ToolTip = styled.small`
    color: ${({ theme }) => theme.primaryGrey};
    display: inline-block;
`;
